import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import {
  MessageSquare,
  RefreshCw,
  Send,
  Sparkles,
  UserCircle2,
  Loader2,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/lib/auth";
import { API } from "@/lib/site";

/**
 * Admin-side inbox for the visitor chat bubble. Polls every 10 s for new
 * conversations, ships browser desktop notifications when the unread total
 * increments, and lets the admin type a reply which (a) is persisted as
 * role="admin" and (b) mutes further AI responses on that session.
 */
export default function ChatInboxPanel() {
  const { headers } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [selectedMeta, setSelectedMeta] = useState(null);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [unread, setUnread] = useState({ unread_messages: 0, unread_sessions: 0 });
  const lastNotifiedCountRef = useRef(0);
  const scrollerRef = useRef(null);

  const requestNotifPermission = useCallback(async () => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission === "default") {
      try {
        await Notification.requestPermission();
      } catch {
        /* user dismissed — nothing to do */
      }
    }
  }, []);

  const notifyDesktop = useCallback((title, body) => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    try {
      const n = new Notification(title, {
        body,
        icon: "/brand/atlasforge-logo.webp",
        tag: "atlasforge-chat",
      });
      n.onclick = () => {
        window.focus();
        n.close();
      };
    } catch {
      /* some browsers throw if the service worker is missing; ignore */
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/chat/admin/sessions`, {
        headers: headers(),
      });
      setSessions(data.sessions || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/chat/admin/unread-count`, {
        headers: headers(),
      });
      setUnread(data);
      // Trigger a desktop notification only when the unread count went UP
      // since the last poll, so refreshes and mark-reads don't spam.
      if (
        data.unread_messages > lastNotifiedCountRef.current &&
        lastNotifiedCountRef.current > 0
      ) {
        notifyDesktop(
          "New AtlasForge chat message",
          `${data.unread_sessions} conversation${data.unread_sessions === 1 ? "" : "s"} waiting for you.`,
        );
      }
      lastNotifiedCountRef.current = data.unread_messages;
    } catch {
      /* silent — badge just stays stale until next poll */
    }
  }, [headers, notifyDesktop]);

  const fetchThread = useCallback(async (sessionId) => {
    if (!sessionId) return;
    try {
      const { data } = await axios.get(
        `${API}/chat/admin/sessions/${sessionId}/messages`,
        { headers: headers() },
      );
      setMessages(data.messages || []);
      setSelectedMeta(data.session || null);
      // Silently clear the unread badge on this session so the count sinks.
      await axios.post(
        `${API}/chat/admin/sessions/${sessionId}/mark-read`,
        {},
        { headers: headers() },
      );
      fetchSessions();
      fetchUnread();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [headers, fetchSessions, fetchUnread]);

  // Initial load + poll every 10s.
  useEffect(() => {
    requestNotifPermission();
    fetchSessions();
    fetchUnread();
    const t = setInterval(() => {
      fetchSessions();
      fetchUnread();
      if (selectedId) fetchThread(selectedId);
    }, 10000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (selectedId) fetchThread(selectedId);
  }, [selectedId, fetchThread]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages]);

  const sendReply = async () => {
    const text = reply.trim();
    if (!text || !selectedId || sending) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/chat/admin/sessions/${selectedId}/reply`,
        { text },
        { headers: headers() },
      );
      setReply("");
      await fetchThread(selectedId);
      toast.success("Reply sent — AI won't respond on this thread anymore.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const draftAiReply = async () => {
    if (!selectedId || drafting) return;
    setDrafting(true);
    try {
      const { data } = await axios.post(
        `${API}/chat/admin/sessions/${selectedId}/draft-reply`,
        {},
        { headers: headers() },
      );
      setReply(data.draft || "");
      toast.success(`Draft ready in ${data.language} — edit and send when you're happy.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDrafting(false);
    }
  };

  const summary = useMemo(() => {
    return {
      total: sessions.length,
      live: sessions.filter((s) => s.human_taken_over).length,
      ai: sessions.filter((s) => !s.human_taken_over).length,
    };
  }, [sessions]);

  return (
    <div
      data-testid="admin-chat-inbox"
      className="mt-8 rounded-2xl border border-slate-200 bg-white af-shadow-card overflow-hidden"
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-2.5">
          <MessageSquare className="h-5 w-5 text-[#0A1128]" />
          <h2 className="font-display text-[18px] text-[#0A1128]">Live chat inbox</h2>
          {unread.unread_messages > 0 && (
            <span
              data-testid="admin-chat-unread-badge"
              className="inline-flex items-center rounded-full bg-rose-100 text-rose-700 text-[11px] font-semibold px-2 py-0.5"
            >
              {unread.unread_messages} new
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-[12px] text-slate-500">
          <span>{summary.total} total</span>
          <span className="text-emerald-600">{summary.live} live w/ human</span>
          <span className="text-slate-400">{summary.ai} AI-only</span>
          <button
            onClick={() => {
              fetchSessions();
              fetchUnread();
              if (selectedId) fetchThread(selectedId);
            }}
            data-testid="admin-chat-refresh"
            className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white text-[12px] font-medium px-2.5 py-1 hover:border-slate-400"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-[280px_1fr] min-h-[420px]">
        {/* Session list */}
        <div className="border-r border-slate-200 max-h-[560px] overflow-y-auto bg-slate-50/50">
          {loading && (
            <div className="p-4 text-[13px] text-slate-500 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading conversations…
            </div>
          )}
          {!loading && sessions.length === 0 && (
            <div className="p-4 text-[13px] text-slate-500">
              No conversations yet. The chat bubble is live on every public page — visitors can start chatting anytime.
            </div>
          )}
          {sessions.map((s) => {
            const active = s.id === selectedId;
            return (
              <button
                key={s.id}
                onClick={() => setSelectedId(s.id)}
                data-testid={`admin-chat-session-${s.id}`}
                className={`w-full text-left px-4 py-3 border-b border-slate-200 transition-colors ${
                  active ? "bg-white" : "hover:bg-white"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-[13.5px] text-[#0A1128] truncate">
                    {s.visitor_name}
                  </p>
                  {s.unread_by_admin > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[10.5px] font-semibold">
                      {s.unread_by_admin}
                    </span>
                  )}
                </div>
                <p className="text-[11.5px] text-slate-500 truncate">{s.visitor_email}</p>
                {s.preview && (
                  <p className="text-[12px] text-slate-600 mt-1.5 line-clamp-2">
                    <span className="text-slate-400 mr-1 uppercase tracking-wider text-[10px] font-semibold">
                      {s.preview.role}
                    </span>
                    {s.preview.text}
                  </p>
                )}
                <div className="mt-1.5 flex items-center gap-1.5">
                  {s.human_taken_over ? (
                    <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-wider text-emerald-700">
                      <UserCircle2 className="h-3 w-3" /> Human-led
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-wider text-slate-500">
                      <Sparkles className="h-3 w-3" /> AI-led
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Thread pane */}
        <div className="flex flex-col max-h-[560px]">
          {!selectedId ? (
            <div className="flex-1 flex items-center justify-center text-[13px] text-slate-500 p-8 text-center">
              Select a conversation on the left to view the transcript and reply.
              <br />
              Sending a reply mutes the AI on that thread automatically.
            </div>
          ) : (
            <>
              {selectedMeta && (
                <div className="px-5 py-3 border-b border-slate-200 bg-white flex items-center justify-between">
                  <div>
                    <p className="text-[14px] font-semibold text-[#0A1128]">
                      {selectedMeta.visitor_name}
                    </p>
                    <p className="text-[12px] text-slate-500">
                      {selectedMeta.visitor_email} · started{" "}
                      {new Date(selectedMeta.created_at).toLocaleString()}
                    </p>
                  </div>
                  {selectedMeta.human_taken_over ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium px-2.5 py-1">
                      <UserCircle2 className="h-3 w-3" /> You own this thread
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 text-[11px] font-medium px-2.5 py-1">
                      <Sparkles className="h-3 w-3" /> AI is auto-answering
                    </span>
                  )}
                </div>
              )}

              <div
                ref={scrollerRef}
                className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-[#F7F9FC]"
                data-testid="admin-chat-thread"
              >
                {messages.map((m) => (
                  <AdminMsg key={m.id} m={m} />
                ))}
              </div>

              <div className="border-t border-slate-200 bg-white p-3 flex flex-col gap-2">
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      sendReply();
                    }
                  }}
                  placeholder="Type your reply… (⌘/Ctrl + Enter to send)"
                  rows={2}
                  data-testid="admin-chat-reply-input"
                  className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-[#0A1128]/40"
                />
                <div className="flex items-center justify-between gap-2">
                  <button
                    onClick={draftAiReply}
                    disabled={drafting || sending}
                    data-testid="admin-chat-draft-ai"
                    title={
                      selectedMeta?.locale && selectedMeta.locale !== "en"
                        ? `Draft a reply in the visitor's language (${selectedMeta.locale.toUpperCase()})`
                        : "Draft a reply using AI"
                    }
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white text-[12.5px] font-medium text-slate-700 px-3 py-2 hover:border-slate-400 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {drafting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Wand2 className="h-3.5 w-3.5" />
                    )}
                    Suggest AI reply
                    {selectedMeta?.locale && selectedMeta.locale !== "en" && (
                      <span className="ml-1 inline-flex items-center rounded-full bg-slate-100 text-[10.5px] font-semibold uppercase tracking-wider px-1.5 py-0.5 text-slate-600">
                        {selectedMeta.locale}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={sendReply}
                    disabled={sending || !reply.trim()}
                    data-testid="admin-chat-reply-send"
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#0A1128] text-white text-[13px] font-medium px-4 py-2.5 hover:bg-[#1E293B] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    Send reply
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AdminMsg({ m }) {
  const isVisitor = m.role === "visitor";
  const isAdmin = m.role === "admin";
  return (
    <div className={`flex ${isVisitor ? "justify-start" : "justify-end"}`}>
      <div
        data-testid={`admin-chat-msg-${m.role}`}
        className={`max-w-[75%] rounded-2xl px-3.5 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-wrap ${
          isVisitor
            ? "bg-white text-slate-800 border border-slate-200 rounded-bl-sm"
            : isAdmin
            ? "bg-emerald-100 text-emerald-950 border border-emerald-200 rounded-br-sm"
            : "bg-[#0A1128] text-white rounded-br-sm"
        }`}
      >
        <p className="text-[10.5px] uppercase tracking-wider mb-1 opacity-70">
          {isVisitor ? "Visitor" : isAdmin ? "You" : "AI assistant"} ·{" "}
          {new Date(m.created_at).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </p>
        {m.text}
      </div>
    </div>
  );
}
